import { useEffect, useState } from "react";

/**
 * Detection for the Cohesive macOS desktop app (packages/user-desktop) —
 * both "are we running inside it?" and "could this browser's machine run it?".
 *
 * The shell appends a stable token to its user agent
 * (packages/user-desktop/src/main.ts), mirroring how the Capacitor container
 * is detected in ./platform.ts.
 *
 * Eligibility for the download prompt requires a macOS desktop browser on
 * Apple silicon running macOS >= MIN_MACOS_MAJOR (the DMG is arm64-only and
 * its Electron runtime sets that floor). How we verify depends on the
 * browser:
 *
 * - Chromium: authoritative — Client Hints report the real macOS version and
 *   CPU architecture (the classic UA string freezes macOS at 10_15_7
 *   everywhere, so it can never answer these).
 * - Safari / Firefox (no Client Hints): best effort — the WebGL renderer
 *   string distinguishes Apple silicon ("Apple GPU" / "Apple M#") from Intel
 *   Macs, and Safari's own version bounds the OS from below (each Safari
 *   major only runs on recent macOS). Firefox's version implies nothing
 *   about macOS, so for it Apple silicon alone is accepted: arm implies
 *   macOS >= 11, and the packaged app's LSMinimumSystemVersion means the
 *   small still-on-11/12 cohort gets macOS's own clear "requires macOS 13"
 *   dialog rather than a confusing failure.
 *
 * Unknowable stays false: Intel-signal or ambiguous GPU strings never
 * prompt, because "not supported on this type of Mac" after a 120MB download
 * is the outcome this gate exists to avoid.
 */

export const DESKTOP_SHELL_UA_TOKEN = "CohesiveDesktop/";

/**
 * Electron 44 (the shell's runtime) supports macOS 13 Ventura and up; the
 * published DMG is arm64-only. Bump alongside Electron upgrades in
 * packages/user-desktop.
 */
export const MIN_MACOS_MAJOR = 13;

/**
 * Safari majors run only on the few most recent macOS releases; Safari 18
 * (Sept 2024) ships for macOS 13 Ventura through 15 Sequoia. So
 * Version/18+ in a Safari UA proves macOS >= 13. Revisit when
 * MIN_MACOS_MAJOR moves.
 */
const SAFARI_MAJOR_FOR_MIN_MACOS = 18;

/**
 * Apple silicon generations bound the OS from below by their ship date:
 * M3 machines shipped with macOS 14. Lets a provably-new machine through
 * even when the browser version can't vouch for the OS.
 */
const CHIP_MAJOR_FOR_MIN_MACOS = 3;

interface UADataValues {
  platform?: string;
  platformVersion?: string;
  architecture?: string;
}

interface UAData {
  platform?: string;
  mobile?: boolean;
  getHighEntropyValues(hints: string[]): Promise<UADataValues>;
}

/** The slice of Navigator the checks read — injectable for tests. */
export interface NavigatorLike {
  userAgent: string;
  maxTouchPoints?: number;
  userAgentData?: UAData;
}

function defaultNavigator(): NavigatorLike | undefined {
  return typeof navigator === "undefined"
    ? undefined
    : (navigator as NavigatorLike);
}

/** True only inside the Cohesive desktop shell. */
export function isDesktopShell(nav = defaultNavigator()): boolean {
  return !!nav?.userAgent.includes(DESKTOP_SHELL_UA_TOKEN);
}

/**
 * The GPU string, unmasked where the browser allows it. On macOS it names
 * either the Apple chip ("Apple M2", "Apple GPU", possibly inside an
 * ANGLE(...) wrapper) or the Intel-era GPU ("Intel Iris...", "AMD
 * Radeon..."). Null when WebGL is unavailable.
 */
export function webglRenderer(): string | null {
  try {
    if (typeof document === "undefined") return null;
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl") ?? canvas.getContext("experimental-webgl");
    if (!gl || !(gl instanceof WebGLRenderingContext)) return null;
    const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
    const value = debugInfo
      ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
      : gl.getParameter(gl.RENDERER);
    return typeof value === "string" ? value : null;
  } catch {
    return null;
  }
}

type GpuFamily = "apple-silicon" | "intel-era" | "unknown";

function classifyRenderer(renderer: string | null): GpuFamily {
  if (!renderer) return "unknown";
  if (/\bApple (GPU|M\d)/.test(renderer)) return "apple-silicon";
  if (/\b(Intel|AMD|Radeon|NVIDIA|GeForce)\b/i.test(renderer)) {
    return "intel-era";
  }
  return "unknown";
}

/** "Apple M3 Pro" → 3; null when the string doesn't name a generation. */
function appleChipMajor(renderer: string | null): number | null {
  const match = renderer?.match(/\bApple M(\d+)/);
  return match ? Number.parseInt(match[1], 10) : null;
}

/** Safari's own major version — the "Version/18.3 … Safari/…" token pair is unique to real Safari. */
function safariMajor(userAgent: string): number | null {
  if (!/\bSafari\//.test(userAgent)) return null;
  const match = userAgent.match(/\bVersion\/(\d+)/);
  return match ? Number.parseInt(match[1], 10) : null;
}

/**
 * Can this browser's machine run the desktop app — and is it not already in
 * it? See the module comment for the per-browser strategy.
 */
export async function evaluateDesktopAppEligibility(
  nav = defaultNavigator(),
  getRenderer: () => string | null = webglRenderer,
): Promise<boolean> {
  if (!nav) return false;
  // Already in the desktop app.
  if (isDesktopShell(nav)) return false;
  // Every macOS desktop browser carries "Macintosh"; phones/tablets/other
  // OSes don't...
  if (!/\bMacintosh\b/.test(nav.userAgent)) return false;
  // ...except iPads with "Request Desktop Website", which present a Mac UA
  // but expose a touch screen.
  if ((nav.maxTouchPoints ?? 0) > 1) return false;

  // Chromium: Client Hints are authoritative for version + architecture.
  const uaData = nav.userAgentData;
  if (uaData) {
    if (uaData.mobile) return false;
    try {
      const values = await uaData.getHighEntropyValues([
        "platformVersion",
        "architecture",
      ]);
      const platform = values.platform ?? uaData.platform;
      if (platform && platform !== "macOS") return false;
      // The DMG is arm64-only — an Intel Mac can't run it at all.
      if (values.architecture !== "arm") return false;
      const major = Number.parseInt(
        values.platformVersion?.split(".")[0] ?? "",
        10,
      );
      return Number.isFinite(major) && major >= MIN_MACOS_MAJOR;
    } catch {
      return false;
    }
  }

  // Safari / Firefox: best effort via GPU + browser-version bounds.
  const renderer = getRenderer();
  if (classifyRenderer(renderer) !== "apple-silicon") return false;

  const chip = appleChipMajor(renderer);
  const provablyNewChip = chip !== null && chip >= CHIP_MAJOR_FOR_MIN_MACOS;

  const safari = safariMajor(nav.userAgent);
  if (safari !== null) {
    // An out-of-date Safari usually means an out-of-date macOS; only let it
    // through when the chip generation independently proves a modern OS.
    return safari >= SAFARI_MAJOR_FOR_MIN_MACOS || provablyNewChip;
  }

  // Firefox (or a hintless Chromium): the browser version can't vouch for
  // the OS. Apple silicon guarantees macOS >= 11; the app's
  // LSMinimumSystemVersion gives the rare 11/12 holdout a clear OS dialog.
  return true;
}

// One evaluation per page load: the answer can't change without a reload.
let cachedEligibility: Promise<boolean> | null = null;

/** False until the (async) check resolves true; stable thereafter. */
export function useDesktopAppEligibility(): boolean {
  const [eligible, setEligible] = useState(false);

  useEffect(() => {
    cachedEligibility ??= evaluateDesktopAppEligibility();
    let alive = true;
    void cachedEligibility.then((value) => {
      if (alive) setEligible(value);
    });
    return () => {
      alive = false;
    };
  }, []);

  return eligible;
}
